// DashboardPage.js
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchDashboard } from "../api/dashboard";
import {
  createCheckin,
  fetchTodayCheckin,
  fetchRecentCheckins,
  fetchCheckinStreak,
} from "../api/checkins";
import {
  fetchActiveSprint,
  submitChallengeShowUp,
} from "../api/trustbuds";
import { createCompletion } from "../api/completions";
import { apiFetch } from "../api/apiFetch";
import { useInterventionCard } from "../intervention/useInterventionCard";
import VisionCreateDialog from "../components/dashboard/VisionCreateDialog";
import CommitmentEditDialog from "../components/dashboard/CommitmentEditDialog";
import TodayPage from "../features/today/TodayPage";
import { dispatchBehaviorChanged } from "../features/behavior/utils/invalidation";
import { useTodayData } from "../features/today/hooks/useTodayData";
import { ACTION_MESSAGE_PARTNER, resolveWebAction } from "../navigation/actionIntents";
import {
  Typography,
  Button,
  Box,
  Paper,
  Stack,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

function fetchNudgeOpportunities({ surface = "", limit = 5 } = {}) {
  const params = new URLSearchParams();
  if (surface) params.set("surface", surface);
  if (limit) params.set("limit", String(limit));
  const query = params.toString();
  return apiFetch(`/nudges/opportunities/${query ? `?${query}` : ""}`);
}

function normalizeActionChooserMode(value = "") {
  const mode = String(value || "").toLowerCase();
  if (["meal_checkin", "plan_next_choice", "start_meal_checkin"].includes(mode)) return "meal_checkin";
  if (["small_version", "minimum_version"].includes(mode)) return "small_version";
  return "full";
}

function actionChooserCopy(mode) {
  if (mode === "meal_checkin") {
    return {
      title: "Start a check-in",
      body: "Pick the habit this check-in is for. This records support without marking the full habit done.",
      button: "Save check-in",
    };
  }
  return {
    title: "Choose one habit",
    body: "Pick the habit you are doing now, then confirm it.",
    button: "Mark done",
  };
}

function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingVision, setCreatingVision] = useState(false);
  const [checkin, setCheckin] = useState(null);
  const [, setCheckinLoading] = useState(false);
  const [, setCheckinError] = useState("");
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [recentCheckinsLoaded, setRecentCheckinsLoaded] = useState(false);
  const [checkinStreak, setCheckinStreak] = useState(0);
  const [commitmentInsights, setCommitmentInsights] = useState([]);
  const [, setCommitmentInsightsLoading] = useState(false);
  const [editingCommitment, setEditingCommitment] = useState(null);
  const [completionOverrides, setCompletionOverrides] = useState({});
  const [insightsVersion, setInsightsVersion] = useState(0);
  const [actionFeedback, setActionFeedback] = useState("");
  const [actionChooserOpen, setActionChooserOpen] = useState(false);
  const [selectedCommitmentId, setSelectedCommitmentId] = useState("");
  const [actionChooserMode, setActionChooserMode] = useState("full");
  const [actionChooserSubmitting, setActionChooserSubmitting] = useState(false);
  const [savingActionKey, setSavingActionKey] = useState("");
  const [activeSprint, setActiveSprint] = useState(null);
  const [recoveryIntelligence, setRecoveryIntelligence] = useState(null);
  const [todayOpportunity, setTodayOpportunity] = useState(null);
  const [todayPayload, setTodayPayload] = useState(null);
  const [trustbudLoading, setTrustbudLoading] = useState(false);
  const [trustbudError, setTrustbudError] = useState("");
  const [discoveryPromptOpen, setDiscoveryPromptOpen] = useState(false);
  const [discoveryPromptSaving, setDiscoveryPromptSaving] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const actionFeedbackTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const onboarding = data?.onboarding || null;
  const intervention = data?.intervention || null;
  const showDominantStateCard = Boolean(intervention?.dominant_nudge);
  const pendingInterventionActionRef = useRef("");
  const {
    beginPrimaryAction: beginInterventionPrimaryAction,
    completePrimaryAction: completeInterventionPrimaryAction,
  } = useInterventionCard({
    intervention,
    surface: "today",
  });

  const startInterventionAction = useCallback(async () => {
    return beginInterventionPrimaryAction();
  }, [beginInterventionPrimaryAction]);

  const queueInterventionCompletion = useCallback((actionKey) => {
    pendingInterventionActionRef.current = actionKey || "";
  }, []);

  const completeQueuedInterventionAction = useCallback(async (actionKey = "") => {
    const targetAction = actionKey || pendingInterventionActionRef.current;
    if (!targetAction) return;
    pendingInterventionActionRef.current = "";
    await completeInterventionPrimaryAction(targetAction);
  }, [completeInterventionPrimaryAction]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchDashboard();
      setData(result);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrustBuds = useCallback(async () => {
    setTrustbudLoading(true);
    setTrustbudError("");
    try {
      const sprint = await fetchActiveSprint();
      setActiveSprint(sprint);
    } catch (error) {
      console.error("Failed to load TrustBuds", error);
      setTrustbudError("Unable to load your Accountability.");
      setActiveSprint(null);
    } finally {
      setTrustbudLoading(false);
    }
  }, []);

  const loadRecoveryIntelligence = useCallback(async () => {
    try {
      const result = await apiFetch("/intelligence/recovery/");
      setRecoveryIntelligence(result?.locked ? null : result || null);
    } catch {
      setRecoveryIntelligence(null);
    }
  }, []);

  const loadTodayOpportunity = useCallback(async () => {
    try {
      const result = await fetchNudgeOpportunities({ surface: "today_card", limit: 1 });
      setTodayOpportunity(result?.opportunities?.[0] || null);
    } catch {
      setTodayOpportunity(null);
    }
  }, []);

  const loadTodayPayload = useCallback(async () => {
    try {
      const result = await apiFetch("/today/");
      setTodayPayload(result || null);
      return result || null;
    } catch (error) {
      console.error("Failed to load Today story", error);
      setTodayPayload(null);
      return null;
    }
  }, []);

  useEffect(() => {
    function handleTrustBudChange() {
      loadDashboard();
    }

    window.addEventListener("trustbud:changed", handleTrustBudChange);
    return () => {
      window.removeEventListener("trustbud:changed", handleTrustBudChange);
    };
  }, [loadDashboard]);

  useEffect(() => {
    loadTrustBuds();
    function handleTrustBudConnections() {
      loadTrustBuds();
    }
    window.addEventListener("trustbud:connections-changed", handleTrustBudConnections);
    return () => {
      window.removeEventListener("trustbud:connections-changed", handleTrustBudConnections);
    };
  }, [loadTrustBuds]);

  useEffect(() => {
    let active = true;
    async function loadDiscoveryPrompt() {
      if (showDominantStateCard) {
        setDiscoveryPromptOpen(false);
        return;
      }
      try {
        const prefs = await apiFetch("/users/me/preferences/");
        if (!active) return;
        if (!prefs?.has_seen_discovery_prompt) {
          setDiscoveryPromptOpen(true);
        }
      } catch {
        if (active) setDiscoveryPromptOpen(false);
      }
    }
    loadDiscoveryPrompt();
    return () => {
      active = false;
    };
  }, [showDominantStateCard]);

  async function handleDiscoveryChoice(openToAccountability) {
    setDiscoveryPromptSaving(true);
    try {
      await apiFetch("/users/me/preferences/", {
        method: "PUT",
        body: JSON.stringify({
          is_available_for_trustbud: openToAccountability,
          has_seen_discovery_prompt: true,
        }),
      });
    } finally {
      setDiscoveryPromptSaving(false);
      setDiscoveryPromptOpen(false);
    }
  }

  const loadCheckin = useCallback(async () => {
    setCheckinLoading(true);
    setCheckinError("");
    try {
      const result = await fetchTodayCheckin();
      setCheckin(result);
    } catch (error) {
      console.error("Failed to load check-in", error);
      setCheckinError("Unable to load today's check-in.");
    } finally {
      setCheckinLoading(false);
    }
  }, []);

  const loadRecentCheckins = useCallback(async () => {
    try {
      const result = await fetchRecentCheckins();
      const list = Array.isArray(result) ? result : [];
      setRecentCheckins(list);
      setRecentCheckinsLoaded(true);
      return list;
    } catch (error) {
      console.error("Failed to load recent check-ins", error);
      setRecentCheckins([]);
      setRecentCheckinsLoaded(true);
      return [];
    }
  }, []);

  const loadCheckinStreak = useCallback(async (updateTarget = true) => {
    try {
      const result = await fetchCheckinStreak();
      const streakValue = Number(result?.streak ?? 0);
      setCheckinStreak(streakValue);
      return streakValue;
    } catch (error) {
      console.error("Failed to load check-in streak", error);
      if (updateTarget) {
        setCheckinStreak(0);
      }
      return 0;
    }
  }, []);

  const loadCommitmentInsights = useCallback(async (visionId) => {
    if (!visionId) {
      setCommitmentInsights([]);
      return;
    }
    setCommitmentInsightsLoading(true);
    try {
      const insights = await apiFetch(`/dashboard/vision/${visionId}/data/`);
      const flattened = (insights?.goals || []).flatMap((goal) => {
        if (goal?.completed_at) return [];
        return (goal.commitments || [])
          .filter((commitment) => commitment?.is_active !== false)
          .map((commitment) => ({
            ...commitment,
            goalId: goal.id,
            goalTitle: goal.title,
          }));
      });
      setCommitmentInsights(flattened);
    } catch (error) {
      console.error("Failed to load commitment insights", error);
      setCommitmentInsights([]);
    } finally {
      setCommitmentInsightsLoading(false);
    }
  }, []);

  const handleCheckin = useCallback(async (status) => {
    setCheckinError("");
    setCheckinLoading(true);
    const optimisticCheckin = { status };
    setCheckin(optimisticCheckin);

    try {
      const result = await createCheckin({ status });
      setCheckin(result);
      setInsightsVersion((prev) => prev + 1);
      await loadCheckinStreak(false);
      await loadRecentCheckins();
      await completeQueuedInterventionAction("quick_checkin");
      dispatchBehaviorChanged({
        eventType: "checkin_created",
        hasSprint: Boolean(activeSprint),
        sourceSurface: "today",
      });
      setActionFeedback(
        intervention?.supporting_context?.success_message || "Nice. That keeps this going."
      );
      await loadTodayPayload();
    } catch (error) {
      console.error("Failed to create check-in", error);
      if (error?.message?.toLowerCase?.().includes("already checked in")) {
        await loadCheckin();
        setCheckinError("");
      } else {
        setCheckin(null);
        setCheckinError(
          error.message || "Unable to save your check-in."
        );
      }
    } finally {
      setCheckinLoading(false);
    }
  }, [
    activeSprint,
    intervention,
    loadRecentCheckins,
    loadCheckin,
    loadCheckinStreak,
    loadTodayPayload,
    completeQueuedInterventionAction,
    setInsightsVersion,
  ]);

  function refreshInsights() {
    setInsightsVersion((prev) => prev + 1);
  }

  async function handleCommitmentComplete(commitment, completionType = "full") {
    if (!commitment?.id) return;
    const normalizedType =
      completionType === "meal_checkin"
        ? "meal_checkin"
        : completionType === "small_version"
          ? "small_version"
          : "full";
    setSavingActionKey(`${commitment.id}:${normalizedType}`);
    setCommitmentInsights((prev) =>
      prev.map((item) => {
        if (item.id !== commitment.id) return item;
        const todayKey = getLocalDateKey(new Date());
        const completions = item.completions || [];
        const alreadyLogged = completions.some(
          (completion) => completion.completed && completion.date === todayKey
        );
        if (alreadyLogged) {
          return item;
        }
        return {
          ...item,
          completions: [
            ...completions,
            {
              completed: true,
              date: todayKey,
              completion_type: normalizedType,
              metadata: { completion_type: normalizedType },
              small_version_title: normalizedType === "small_version" ? commitment.minimum_version_title || "" : "",
            },
          ],
        };
      })
    );
    setCompletionOverrides((prev) => ({
      ...prev,
      [commitment.id]: true,
    }));
    try {
      const completionResult = await createCompletion(commitment.id, {
        completion_type: normalizedType,
        source: "web_today",
      });
      await completeQueuedInterventionAction("complete_due_action");
      const feedback = completionResult?.coach_communication || completionResult?.completion_feedback;
      if (feedback?.title || feedback?.body) {
        setActionFeedback([feedback.title, feedback.body].filter(Boolean).join(" "));
      } else if (normalizedType === "meal_checkin") {
        setActionFeedback(`${commitment.title} check-in saved. It does not mark the full habit done.`);
      } else if (normalizedType === "small_version") {
        setActionFeedback(
          `${commitment.minimum_version_title || commitment.title} kept the habit alive today.`
        );
      } else {
        setActionFeedback(`${commitment.title} is done for today.`);
      }
      await loadTodayPayload();
      dispatchBehaviorChanged({
        eventType:
          normalizedType === "meal_checkin"
            ? "meal_checkin_completed"
            : normalizedType === "small_version"
              ? "small_version_completed"
              : "action_completed",
        hasSprint: Boolean(activeSprint),
        sourceSurface: "today",
      });
    } catch (error) {
      setCompletionOverrides((prev) => ({
        ...prev,
        [commitment.id]: false,
      }));
      if (data?.vision?.id) {
        await loadCommitmentInsights(data.vision.id);
      }
      setActionFeedback(error?.message || "Unable to save that habit.");
    } finally {
      setSavingActionKey("");
    }
  }

  async function handleSpecificCommitmentComplete(commitmentId, completionType = "full") {
    const selected = dueCommitments.find(
      (item) => String(item.id) === String(commitmentId)
    );
    if (!selected) {
      openActionChooser({ preferredCommitmentId: commitmentId });
      return;
    }

    await handleCommitmentComplete(selected, completionType);
  }

  async function handleActionResetToday(commitmentId) {
    setSavingActionKey(`${commitmentId}:reset`);
    try {
      if (activeSprint?.id) {
        await submitChallengeShowUp(activeSprint.id, "reset");
        await loadTrustBuds();
        await loadTodayPayload();
        setActionFeedback("Reset recorded. You stayed visible, and it does not count as done.");
        dispatchBehaviorChanged({
          eventType: "reset_submitted",
          hasSprint: true,
          sourceSurface: "today",
        });
      } else {
        await handleCheckin(true);
        setActionFeedback("Reset recorded. Use Done when one habit actually gets done.");
      }
    } catch (error) {
      setActionFeedback(error?.message || "Unable to save that reset.");
    } finally {
      setSavingActionKey("");
    }
  }

  const closeActionChooser = useCallback(() => {
    if (actionChooserSubmitting) return;
    setActionChooserOpen(false);
    setSelectedCommitmentId("");
    setActionChooserMode("full");
  }, [actionChooserSubmitting]);

  async function handleConfirmChosenCommitment() {
    const selected = dueCommitments.find(
      (item) => String(item.id) === String(selectedCommitmentId)
    );
    if (!selected) return;

    setActionChooserSubmitting(true);
    try {
      await handleCommitmentComplete(selected, actionChooserMode);
      closeActionChooser();
    } finally {
      setActionChooserSubmitting(false);
    }
  }

  useEffect(() => {
    loadDashboard();
    loadRecoveryIntelligence();
    loadTodayOpportunity();
    loadTodayPayload();
    loadCheckin();
    loadRecentCheckins();
    loadCheckinStreak();
  }, [loadDashboard, loadRecoveryIntelligence, loadTodayOpportunity, loadTodayPayload, loadCheckin, loadRecentCheckins, loadCheckinStreak]);


  useEffect(() => {
    if (data?.vision?.id) {
      loadCommitmentInsights(data.vision.id);
    }
  }, [data?.vision?.id, insightsVersion, loadCommitmentInsights]);

  useEffect(() => {
    if (!commitmentInsights.length) {
      setCompletionOverrides({});
      return;
    }
    const todayKey = getLocalDateKey(new Date());
    const next = commitmentInsights.reduce((acc, commitment) => {
      acc[commitment.id] = (commitment.completions || []).some(
        (completion) => completion.completed && completion.date === todayKey
      );
      return acc;
    }, {});
    setCompletionOverrides(next);
  }, [commitmentInsights]);

  useEffect(() => {
    if (!actionFeedback) return;
    if (actionFeedbackTimerRef.current) {
      clearTimeout(actionFeedbackTimerRef.current);
    }
    actionFeedbackTimerRef.current = setTimeout(() => {
      setActionFeedback("");
      actionFeedbackTimerRef.current = null;
    }, 3600);
  }, [actionFeedback]);

  function getLocalDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function getWeekdayIndex(date = new Date()) {
    return (date.getDay() + 6) % 7;
  }

  const isActiveOnDate = useCallback((commitment, date) => {
    const activeDays = Array.isArray(commitment.active_days)
      ? commitment.active_days
      : [0, 1, 2, 3, 4, 5, 6];
    return activeDays.includes(getWeekdayIndex(date));
  }, []);

  const completionOverrideMap = completionOverrides;
  const activeCommitments = commitmentInsights.filter(
    (commitment) => commitment.is_active
  );

  const visibleTodayCommitments = useMemo(() => {
    const today = new Date();
    return commitmentInsights.filter(
      (item) =>
        item.is_active &&
        (item.frequency === "weekly" ||
          item.frequency === "monthly" ||
          (item.frequency === "daily" && isActiveOnDate(item, today)))
    );
  }, [commitmentInsights, isActiveOnDate]);

  const dueCommitments = useMemo(
    () =>
      visibleTodayCommitments.filter(
        (item) => !completionOverrideMap[item.id] && !item.completed_in_window
      ),
    [completionOverrideMap, visibleTodayCommitments]
  );

  function openActionChooser(options = {}) {
    if (!dueCommitments.length) {
      navigate("/plan");
      return;
    }
    setActionChooserMode(normalizeActionChooserMode(options.mode));
    const preferredId = options.preferredCommitmentId
      ? String(options.preferredCommitmentId)
      : "";
    const fallbackId =
      preferredId && dueCommitments.some((item) => String(item.id) === preferredId)
        ? preferredId
        : String(dueCommitments[0]?.id || "");
    setSelectedCommitmentId(fallbackId);
    setActionChooserOpen(true);
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusedHabitId = params.get("habitId") || params.get("commitmentId") || params.get("actionId");
    if (params.get("focus") !== "action-picker" && !focusedHabitId) return;
    if (actionChooserOpen) return;

    const preferredCommitmentId = focusedHabitId;
    const requestedMode = normalizeActionChooserMode(params.get("mode"));
    if (!dueCommitments.length) {
      navigate("/plan", { replace: true });
      return;
    }
    const preferredId = preferredCommitmentId ? String(preferredCommitmentId) : "";
    const fallbackId =
      preferredId && dueCommitments.some((item) => String(item.id) === preferredId)
        ? preferredId
        : String(dueCommitments[0]?.id || "");
    setSelectedCommitmentId(fallbackId);
    setActionChooserMode(requestedMode);
    setActionChooserOpen(true);

    params.delete("focus");
    params.delete("actionId");
    params.delete("habitId");
    params.delete("commitmentId");
    params.delete("date");
    params.delete("mode");
    params.delete("source");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [actionChooserOpen, dueCommitments, location.pathname, location.search, navigate]);

  const visionGoals = data?.vision?.goals;
  const todayGoals = useMemo(
    () => (Array.isArray(visionGoals) ? visionGoals : []),
    [visionGoals]
  );

  useEffect(() => {
    if (!todayGoals.length) {
      setSelectedGoalId(null);
      return;
    }
    if (selectedGoalId === null || selectedGoalId === undefined || String(selectedGoalId) === "__all_goals__") {
      return;
    }
    const activeGoals = todayGoals.filter((goal) => !goal?.completed_at);
    const goalPool = activeGoals.length ? activeGoals : todayGoals;
    const hasSelectedGoal = goalPool.some(
      (goal) => String(goal?.id) === String(selectedGoalId)
    );
    if (!hasSelectedGoal) {
      setSelectedGoalId(null);
    }
  }, [selectedGoalId, todayGoals]);

  useEffect(() => {
    if (!recentCheckinsLoaded) return;
    loadCheckinStreak();
  }, [recentCheckinsLoaded, loadCheckinStreak]);

  const todaysCommitments = activeCommitments
    .filter(
      (commitment) =>
        commitment.frequency === "weekly" ||
        commitment.frequency === "monthly" ||
        (commitment.frequency === "daily" && isActiveOnDate(commitment, new Date()))
    )
    .map((commitment) => ({
      ...commitment,
      completed_today: completionOverrideMap[commitment.id],
      completed_in_window: Boolean(commitment.completed_in_window || completionOverrideMap[commitment.id]),
    }))
    .sort((a, b) => {
      const goalCompare = (a.goalTitle || "").localeCompare(b.goalTitle || "");
      if (goalCompare !== 0) return goalCompare;
      const titleCompare = (a.title || "").localeCompare(b.title || "");
      if (titleCompare !== 0) return titleCompare;
      return a.id - b.id;
    });

  const todayModel = useTodayData({
    summary: data?.summary || null,
    todayPayload,
    behaviorSummary: data?.behavior_summary || data?.summary?.behavior_summary || null,
    intervention,
    checkin,
    recentCheckins,
    checkinStreak,
    activeCommitments,
    todaysCommitments,
    dueCommitments,
    completionOverrideMap,
    activeSprint,
    goals: todayGoals,
    selectedGoalId,
    onSelectGoal: setSelectedGoalId,
    onChooseAction: () => openActionChooser(),
    onCheckIn: () => handleCheckin(true),
    onSendUpdate: () => {
      const route = resolveWebAction(ACTION_MESSAGE_PARTNER, {
        partnerName: activeSprint?.other_user?.username || "",
        partnerUserId: intervention?.partner_summary?.id || activeSprint?.other_user?.id || null,
        sprintId: onboarding?.sprint_id || activeSprint?.id || null,
        connectionId: activeSprint?.connection_id || activeSprint?.connection?.id || null,
        source: "today",
      }).route;
      navigate(route || "/activity?tab=messages");
    },
    onOpenPlan: () => navigate("/plan"),
    onOpenSprint: () => navigate("/sprint"),
    onReviewSprint: () => navigate("/sprint"),
    onBeginInterventionAction: startInterventionAction,
    onQueueInterventionCompletion: queueInterventionCompletion,
    onCompleteInterventionAction: completeQueuedInterventionAction,
  });

  if (loading && !data) {
    return (
      <Box
        sx={{
          minHeight: "40vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  // ─────────────────────────────────────────────
  // EMPTY STATE: No vision yet
  // ─────────────────────────────────────────────
  if (!data?.vision) {
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2.5,
            mb: 3,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Start with a vision, then add one goal and one repeatable habit.
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => setCreatingVision(true)}
          >
            Create vision
          </Button>
        </Paper>

        {creatingVision && (
          <VisionCreateDialog
            onClose={() => setCreatingVision(false)}
            onSaved={() => {
              setCreatingVision(false);
              loadDashboard();
              navigate("/plan");
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1.5, sm: 0 } }}>
      <TodayPage
        model={todayModel}
        onActionClick={handleSpecificCommitmentComplete}
        onResetToday={handleActionResetToday}
        savingActionKey={savingActionKey}
        onSendUpdate={() => {
          const route = resolveWebAction(ACTION_MESSAGE_PARTNER, {
            partnerName: activeSprint?.other_user?.username || "",
            partnerUserId: intervention?.partner_summary?.id || activeSprint?.other_user?.id || null,
            sprintId: onboarding?.sprint_id || activeSprint?.id || null,
            connectionId: activeSprint?.connection_id || activeSprint?.connection?.id || null,
            source: "today",
          }).route;
          navigate(route || "/activity?tab=messages");
        }}
        onOpenPlan={() => navigate("/plan")}
        onOpenSprint={() => navigate("/sprint")}
        onOpenConnections={() => navigate("/connections")}
        onOpenWeeklySummary={() => navigate("/weekly-summary")}
        recoveryIntelligence={recoveryIntelligence}
        todayOpportunity={todayOpportunity || data?.nudge_opportunity || null}
        onOpportunityAction={(opportunity) => {
          const deeplink = opportunity?.deeplink || "";
          if (deeplink.startsWith("/")) {
            navigate(deeplink);
            return;
          }
          if (opportunity?.intent === "reach_out") {
            navigate("/activity?tab=messages");
            return;
          }
          if (opportunity?.intent === "sprint_accountability") {
            navigate("/sprint");
            return;
          }
          navigate("/plan");
        }}
        onRecoveryAction={() => {
          apiFetch("/intelligence/feedback/", {
            method: "POST",
            body: JSON.stringify({
              action: "recovery_completed",
              task_type: "recovery_coaching",
            }),
          }).catch(() => {});
          openActionChooser({ mode: "full" });
        }}
        feedback={actionFeedback}
        sprintLoading={trustbudLoading}
        sprintError={trustbudError}
      />

      <Dialog
        open={actionChooserOpen}
        onClose={closeActionChooser}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{actionChooserCopy(actionChooserMode).title}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionChooserCopy(actionChooserMode).body}
          </Typography>
          <Stack spacing={1}>
            {dueCommitments.map((commitment) => {
              const active = String(commitment.id) === String(selectedCommitmentId);
              return (
                <Paper
                  key={commitment.id}
                  variant="outlined"
                  onClick={() => setSelectedCommitmentId(String(commitment.id))}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    borderColor: active ? "text.primary" : "divider",
                    bgcolor: active ? "grey.50" : "background.paper",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {commitment.title}
                      </Typography>
                      {commitment.goalTitle ? (
                        <Typography variant="caption" color="text.secondary">
                          For {commitment.goalTitle}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: active ? "text.primary" : "divider",
                        bgcolor: active ? "text.primary" : "background.paper",
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeActionChooser} disabled={actionChooserSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmChosenCommitment}
            disabled={!selectedCommitmentId || actionChooserSubmitting}
          >
            {actionChooserSubmitting
              ? "Saving..."
              : actionChooserCopy(actionChooserMode).button}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={discoveryPromptOpen}
        onClose={() => {
          if (!discoveryPromptSaving) handleDiscoveryChoice(false);
        }}
      >
        <DialogTitle>Privacy & Discovery</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Do you want to be discoverable so others can find you as a TrustBud?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleDiscoveryChoice(false)}
            disabled={discoveryPromptSaving}
          >
            Not right now
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDiscoveryChoice(true)}
            disabled={discoveryPromptSaving}
          >
            Yes, I'm open to accountability
          </Button>
        </DialogActions>
      </Dialog>

      {editingCommitment && (
        <CommitmentEditDialog
          commitment={editingCommitment}
          onClose={() => setEditingCommitment(null)}
          onSaved={(updated) => {
            setEditingCommitment(null);
            if (updated?.id) {
              setCommitmentInsights((prev) =>
                prev.map((item) =>
                  item.id === updated.id
                    ? { ...item, ...updated }
                    : item
                )
              );
            }
            loadDashboard();
            refreshInsights();
          }}
        />
      )}
    </Box>
  );
}

export default DashboardPage;
